import { Link } from '@tanstack/react-router';

import { DisplayHeading, Eyebrow } from '@/components/editorial';
import type { PublicWritingRecord } from '@/content/schemas';
import type { WritingCategory } from '@/content/registry';

import { getCategoryDescription, getCategoryLabel } from '../categories';
import { WritingCategoryNav, WritingListCard } from './writing-list-card';

type WritingCategoryPageProps = {
  category: WritingCategory;
  posts: PublicWritingRecord[];
  activeCategories: WritingCategory[];
};

export function WritingCategoryPage({
  category,
  posts,
  activeCategories,
}: WritingCategoryPageProps) {
  const label = getCategoryLabel(category);
  const description = getCategoryDescription(category);

  return (
    <div className="container max-w-5xl px-6 py-16 md:py-24">
      <div className="mb-8">
        <Link
          to="/blog"
          className="text-muted-foreground hover:text-brand font-mono text-xs transition-colors"
        >
          ← All posts
        </Link>
      </div>

      <div className="mb-14">
        <Eyebrow className="mb-4">Category</Eyebrow>
        <DisplayHeading as="h1" size="xl" animate={false}>
          {label}
        </DisplayHeading>
        <p className="text-muted-foreground mt-4 max-w-xl text-lg leading-relaxed">
          {description}
        </p>
        <p className="text-muted-foreground mt-3 font-mono text-xs">
          {posts.length} {posts.length === 1 ? 'post' : 'posts'}
        </p>
      </div>

      <WritingCategoryNav
        activeCategories={activeCategories}
        currentCategory={category}
      />

      {posts.length === 0 ? (
        <p className="text-muted-foreground py-16 text-center font-mono text-sm">
          No posts in this category yet.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {posts.map((post, index) => (
            <WritingListCard key={post.id} post={post} index={index + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
